import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { alertStyle } from "@/components/AlertMarker";
import { metersBetween } from "@/lib/bbox";
import type { WazeAlert } from "@shared/types";

interface AlertListProps {
  alerts: WazeAlert[];
  origin: { lat: number; lon: number } | null;
  onSelect?: (alert: WazeAlert) => void;
  loading?: boolean;
}

function formatDistance(m: number): string {
  if (!Number.isFinite(m)) return "";
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(1)} km`;
}

function formatAge(ms: number): string {
  const sec = Math.max(0, (Date.now() - ms) / 1000);
  if (sec < 60) return `${Math.round(sec)}s ago`;
  const min = sec / 60;
  if (min < 60) return `${Math.round(min)}m ago`;
  const hr = min / 60;
  return `${hr.toFixed(1)}h ago`;
}

export function AlertList({ alerts, origin, onSelect, loading }: AlertListProps) {
  const ranked = alerts
    .map((a) => ({
      alert: a,
      distance:
        origin && a.location
          ? metersBetween(origin, { lat: a.location.y, lon: a.location.x })
          : Number.POSITIVE_INFINITY,
    }))
    .sort((a, b) => a.distance - b.distance);

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          variant="glass"
          size="icon"
          className="flex-col gap-0 px-2 leading-tight"
        >
          <span className="text-base font-semibold">{alerts.length}</span>
          <span className="text-xs text-muted-foreground">alerts</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="bottom" className="max-h-[70vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Nearby alerts</SheetTitle>
          <SheetDescription>
            {loading
              ? "Updating…"
              : `${alerts.length} reports in view, sorted by distance`}
          </SheetDescription>
        </SheetHeader>

        <ul className="mt-4 flex flex-col gap-2">
          {ranked.length === 0 && (
            <li className="rounded-xl border border-border bg-card/40 p-4 text-center text-muted-foreground">
              All clear out there.
            </li>
          )}
          {ranked.map(({ alert, distance }) => {
            const style = alertStyle(alert);
            const Icon = style.icon;
            return (
              <li key={alert.uuid}>
                <button
                  type="button"
                  onClick={() => onSelect?.(alert)}
                  className="flex w-full items-center gap-4 rounded-xl border border-border bg-card/60 p-4 text-left transition active:scale-[0.99] hover:bg-card"
                >
                  <span
                    className={`flex size-12 items-center justify-center rounded-full ${style.color}`}
                  >
                    <Icon className="size-6" />
                  </span>
                  <span className="flex flex-1 flex-col">
                    <span className="flex items-center justify-between gap-2 text-base font-semibold">
                      <span>{style.label}</span>
                      <span className="text-sm font-normal text-muted-foreground">
                        {formatDistance(distance)}
                      </span>
                    </span>
                    <span className="text-sm text-muted-foreground line-clamp-1">
                      {alert.street ?? alert.city ?? "Unknown street"}
                    </span>
                    <span className="text-xs text-muted-foreground/80">
                      {formatAge(alert.pubMillis)}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </SheetContent>
    </Sheet>
  );
}
